import React, { useState, useEffect } from 'react';
import './LogoCarousel.css';
import { createApiUrl } from '../utils/apiConfig';

const LogoCarousel = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [logos, setLogos] = useState([]);

  // Load logos dynamically
  useEffect(() => {
    const loadLogos = async () => {
      try {
        // Use the centralized API configuration
        const apiUrl = createApiUrl('/api/logos');
        
        // Fetch logos from the backend API
        const response = await fetch(apiUrl, {
          method: 'GET',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
          },
        });
        
        if (!response.ok) {
          throw new Error(`Failed to fetch logos: ${response.status}`);
        }
        
        const data = await response.json();
        setLogos(data.logos || []);
      } catch (error) {
        console.error('Error loading logos:', error);
        // If API fails, show empty array - logos will be loaded when API is available
        setLogos([]);
      }
    };

    loadLogos();
  }, []);

  const MAX_LOGOS_PER_VIEW = 9;
  
  // Only show scrollable carousel if we have more logos than can fit in one view
  const shouldShowCarousel = logos.length > MAX_LOGOS_PER_VIEW;
  
  // Dynamically adjust logos per view based on available logos
  const logosPerView = Math.min(logos.length, MAX_LOGOS_PER_VIEW);

  // Auto-scroll effect - start after a short delay when logos are loaded
  useEffect(() => {
    if (logos.length === 0 || !shouldShowCarousel) return;
    
    // Start auto-scroll after 2 seconds to let users see the initial logos
    const startDelay = setTimeout(() => {
      const interval = setInterval(() => {
        setCurrentIndex((prevIndex) => (prevIndex + logosPerView) % logos.length);
      }, 3000); // Change logos every 3 seconds
      
      // Store interval ID for cleanup
      window.logoCarouselInterval = interval;
    }, 3000); // Wait 3 seconds before starting auto-scroll
    
    return () => {
      clearTimeout(startDelay);
      if (window.logoCarouselInterval) {
        clearInterval(window.logoCarouselInterval);
        window.logoCarouselInterval = null;
      }
    };
  }, [logos.length, logosPerView, shouldShowCarousel]); // Depend on shouldShowCarousel as well

  const nextSlide = () => {
    if (isTransitioning || logos.length === 0 || !shouldShowCarousel) return;
    setIsTransitioning(true);
    setCurrentIndex((prevIndex) => (prevIndex + logosPerView) % logos.length);
    setTimeout(() => setIsTransitioning(false), 500);
  };

  const prevSlide = () => {
    if (isTransitioning || logos.length === 0 || !shouldShowCarousel) return;
    setIsTransitioning(true);
    setCurrentIndex((prevIndex) => (prevIndex - logosPerView + logos.length) % logos.length);
    setTimeout(() => setIsTransitioning(false), 500);
  };

  // Don't render anything if there are no logos
  if (logos.length === 0) {
    return null;
  }

  return (
    <div className="logo-carousel-container">
      <h5 className="text-center mb-3">Schools & Partners</h5>
      <div 
        className="logo-carousel"
        style={{
          maxWidth: shouldShowCarousel ? '95%' : `${logos.length * 140}px`
        }}
      >
        {shouldShowCarousel && (
          <>
            <button 
              className="carousel-control prev" 
              onClick={prevSlide}
              aria-label="Previous logos"
            >
              <i className="fas fa-chevron-left"></i>
            </button>
            <div 
              className="carousel-track"
              style={{
                maxWidth: '1200px'
              }}
            >
              <div className="carousel-slides-container">
                {Array.from({ length: logosPerView }, (_, index) => {
                  const logoIndex = (currentIndex + index) % logos.length;
                  const logo = logos[logoIndex];
                  return (
                    <div key={`${currentIndex}-${index}`} className="carousel-slide">
                      <div className="logo-item">
                        <img 
                          src={logo.logo} 
                          alt={logo.alt}
                          onError={(e) => {
                            console.error('Logo failed to load:', logo.logo);
                            e.target.style.display = 'none';
                            const placeholder = e.target.nextSibling;
                            if (placeholder) {
                              placeholder.style.display = 'block';
                            }
                          }}
                          onLoad={() => {
                            
                          }}
                          loading="lazy"
                        />
                        <div className="logo-placeholder" style={{ display: 'none' }}>
                          {logo.name}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            <button 
              className="carousel-control next" 
              onClick={nextSlide}
              aria-label="Next logos"
            >
              <i className="fas fa-chevron-right"></i>
            </button>
          </>
        )}
        {!shouldShowCarousel && (
          <div className="carousel-slides-container">
            {logos.map((logo, index) => (
              <div key={index} className="carousel-slide">
                <div className="logo-item">
                  <img 
                    src={logo.logo} 
                    alt={logo.alt}
                    onError={(e) => {
                      console.error('Logo failed to load:', logo.logo);
                      e.target.style.display = 'none';
                      const placeholder = e.target.nextSibling;
                      if (placeholder) {
                        placeholder.style.display = 'block';
                      }
                    }}
                    onLoad={() => {
                      
                    }}
                    loading="lazy"
                  />
                  <div className="logo-placeholder" style={{ display: 'none' }}>
                    {logo.name}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      {/* Indicators: show one for each page */}
      {shouldShowCarousel && (
        <div className="carousel-indicators">
          {Array.from({ length: Math.ceil(logos.length / logosPerView) }, (_, i) => {
            const pageStart = i * logosPerView;
            return (
              <button
                key={i}
                className={`indicator ${Math.floor(currentIndex / logosPerView) === i ? 'active' : ''}`}
                onClick={() => setCurrentIndex(pageStart)}
                aria-label={`Go to page ${i + 1}`}
              />
            );
          })}
        </div>
      )}
    </div>
  );
};

export default LogoCarousel; 